import { useEffect, useState } from "react";
import { X, ArrowRight, Clock, Lock, Sparkles } from "lucide-react";

const STORAGE_KEY = "solmis_exit_intent_seen_v1";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Already seen this session → never show again
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    // Disable on mobile / touch devices (mouseleave unreliable)
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) return;

    let timer;
    const onMouseLeave = (e) => {
      // Trigger only when leaving via top of viewport (intent to close tab)
      if (e.clientY > 10) return;
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      // Light debounce
      timer = setTimeout(() => {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setOpen(true);
      }, 120);
    };

    // Activate only after 15s on site (avoids instant trigger)
    const armTimeout = setTimeout(() => {
      document.addEventListener("mouseleave", onMouseLeave);
    }, 15000);

    return () => {
      clearTimeout(timer);
      clearTimeout(armTimeout);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-popup-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[hsl(var(--brand-charcoal)/0.55)] backdrop-blur-sm animate-fade-in"
        onClick={() => setOpen(false)}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-xl bg-white border border-border shadow-[0_30px_80px_-20px_rgba(47,45,42,0.45)] animate-pop-in"
        style={{ animation: "popIn 380ms cubic-bezier(0.22, 1, 0.36, 1)" }}
      >
        {/* Close button */}
        <button
          onClick={() => setOpen(false)}
          data-testid="exit-popup-close"
          aria-label="Fermer"
          className="absolute top-4 right-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-primary/60 hover:text-primary hover:bg-[hsl(var(--brand-beige))] transition-colors"
        >
          <X size={18} strokeWidth={1.8} />
        </button>

        {/* Top accent band */}
        <div className="h-1 bg-gradient-to-r from-[hsl(var(--brand-terra))] via-[hsl(var(--brand-terra))] to-[hsl(var(--brand-kaki))]" />

        <div className="px-8 md:px-12 pt-10 pb-9">
          {/* Eyebrow with icon */}
          <div className="flex items-center gap-2.5 mb-5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--brand-terra)/0.12)] text-[hsl(var(--brand-terra))]">
              <Sparkles size={16} strokeWidth={1.8} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
              Avant de partir
            </span>
          </div>

          {/* Headline */}
          <h2
            id="exit-popup-title"
            className="font-display text-2xl md:text-3xl text-primary leading-[1.2]"
          >
            Votre projet mérite{" "}
            <span className="italic font-light">une réponse claire</span>.
          </h2>

          {/* Body */}
          <p className="mt-4 text-base text-primary/85 leading-relaxed">
            Recevez un <strong>devis gratuit & sans engagement</strong> sous{" "}
            <strong>24 h ouvrées</strong>, avec une première analyse de
            faisabilité incluse. C'est rapide, c'est précis — et ça ne vous
            engage à rien.
          </p>

          {/* Mini-reassurance row */}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-primary/75 font-medium">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} strokeWidth={1.8} className="text-[hsl(var(--brand-terra))]" />
              Réponse 24 h
            </span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Lock size={13} strokeWidth={1.8} className="text-[hsl(var(--brand-terra))]" />
              Vos données protégées
            </span>
            <span className="text-border">·</span>
            <span>+100 projets accompagnés</span>
          </div>

          {/* CTAs */}
          <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="exit-popup-cta"
              className="btn-primary justify-center group flex-1"
            >
              Obtenir mon devis gratuit
              <ArrowRight
                size={17}
                strokeWidth={1.8}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="exit-popup-dismiss"
              className="text-sm text-muted-foreground hover:text-primary transition-colors px-4 py-2"
            >
              Non merci, je continue
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from { opacity: 0; transform: translateY(8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in {
          animation: fadeIn 260ms ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; } to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
